import React, { useContext, useEffect, useState } from 'react'
import { ReactComponent as ContentPostIcon } from '../../assets/svg/ContentPostIcon.svg'
import { ReactComponent as ContentQuestionIcon } from '../../assets/svg/ContentQuestionIcon.svg'
import { ReactComponent as ContentPollIcon } from '../../assets/svg/ContentPollIcon.svg'
import { ReactComponent as ContentSurveyIcon } from '../../assets/svg/ContentSurveyIcon.svg'
import ContentPost from './ContentPost'
import ContentQuestion from './ContentQuestion'
import ContentPoll from './ContentPoll'
import ContentSurvey from './ContentSurvey'
import { getById } from '../../server/server'
import { capitaliseFirstLetter, usePageTitle, useQuery } from '../../utils/util'
import { oneOfCodec, useUrlState } from '../../hooks/useUrlState'
import { ContentContext } from './ContentContext'
import CreateOwnContent from './CreateOwnContent'
import { useTranslation } from 'react-i18next'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'

const typeList = ['feed', 'question', 'poll', 'survey']

function Content(props) {
    usePageTitle('Upstrive Education - Create your own content')
    const query = useQuery()
    const surveyId = query?.get('surveyId')
    const singleProgram = query?.get('singleProgram')
    const contentEdit = query?.get('contentEdit')
    const editId = query?.get('editId')
    // When ?editId=/?surveyId= is present the server response decides the type,
    // overriding whatever ?type= said; the hook then writes the result back.
    const [type, setType] = useUrlState('type', 'feed', oneOfCodec(typeList))
    const [editMode, setEditMode] = useState(!!editId)
    const [refreshContent, setRefreshContent] = useState(Date.now())
    const [contentItem, setContentItem] = useContext(ContentContext)
    const [content, setContent] = useState(singleProgram ? contentItem : {})
    const { t, locale } = useTranslation()
    const typeEvents = {
        feed: ANALYTICS_EVENTS.CONTENT_CREATE_FEED_VIEWED,
        question: ANALYTICS_EVENTS.CONTENT_CREATE_QUESTION_VIEWED,
        poll: ANALYTICS_EVENTS.CONTENT_CREATE_POLL_VIEWED,
        survey: ANALYTICS_EVENTS.CONTENT_CREATE_SURVEY_VIEWED,
    }
    useTrackedSurface(typeEvents[type], {
        contentId: content?.id || editId || surveyId,
        content: content?.title || content?.name,
    })

    // const typeMap = {
    // 	feed: <ContentPost content={content} />,
    // 	question: <ContentQuestion content={content} />,
    // 	poll: <ContentPoll content={content} />,
    // 	survey: <ContentSurvey content={content} />,
    // };
    useEffect(() => {
        if (editId) getSingleContent()
        else if (surveyId) getSurvey()
        else if (contentEdit) {
            setContent(contentItem)
            setType(contentEdit)
        }
        // setContentItem({});
    }, [])

    const getSurvey = async () => {
        const resp = await getById({
            collection: 'surveys',
            id: surveyId,
        })
        if (resp.status === 200) {
            setContent(resp?.data)
            setType('survey')
        }
    }
    const getSingleContent = async () => {
        const resp = await getById({
            collection: 'content',
            id: editId,
        })
        if (resp.status === 200) {
            setContent(resp?.data)
            setType(resp?.data?.type)
        }
    }
    // Icons inherit the tab's text colour so hover/selected states are pure CSS.
    const typeIcon = (type) => {
        switch (type) {
            case 'feed':
                return <ContentPostIcon fill="currentColor" aria-hidden="true" />
            case 'question':
                return <ContentQuestionIcon fill="currentColor" aria-hidden="true" />
            case 'poll':
                return <ContentPollIcon fill="currentColor" aria-hidden="true" />
            case 'survey':
                return <ContentSurveyIcon fill="currentColor" aria-hidden="true" />
            default:
                return null
        }
    }
    // Clicking the active tab still remounts the form: it is the way out of
    // edit mode without leaving the page.
    const handleTypeChange = (nextType) => {
        setRefreshContent(Date.now())
        setType(nextType)
        setContent({})
        setContentItem({})
        setEditMode(false)
    }

    return (
        <div className="page-shell bg-[#ECEBF8] h-full flex justify-center overflow-y-scroll" id="scroll-inf-target">
            <div className="bg-white w-[800px] rounded-xl flex flex-col px-14 h-max">
                <div className="w-full flex flex-col">
                    <h1 className="page-title font-bold text-darkPurple mt-6">{t('createYourOwnContent')} </h1>
                    <div className="flex flex-row w-full justify-between mt-6">
                        {typeList.map((typeContent) => {
                            const selected = typeContent === type
                            return (
                                <button
                                    type="button"
                                    id={`content-type-button-${typeContent}`}
                                    className={`coc-tab pointer flex relative flex-row items-center justify-center rounded-full w-[160px] py-[5px] border ${
                                        selected ? 'bg-deepPurple border-deepPurple' : 'border-deselectPurple bg-whiteContrast'
                                    }`}
                                    key={typeContent}
                                    aria-pressed={selected}
                                    onClick={() => handleTypeChange(typeContent)}
                                >
                                    {typeIcon(typeContent)}
                                    <span className="text-lg font-semibold ml-2">{capitaliseFirstLetter(t(typeContent))}</span>
                                    {selected && (
                                        <div
                                            className="coc-tab-caret w-0 h-0 absolute"
                                            aria-hidden="true"
                                            style={{
                                                borderRight: '10px solid transparent',
                                                borderLeft: '10px solid transparent',
                                                borderTop: '10px solid #5E639B',
                                                // Anchored to the padding-box bottom so it sits flush on
                                                // the border regardless of line-height; a fixed 40px left a gap.
                                                top: '100%',
                                            }}
                                        ></div>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                </div>
                <div className="pt-6 pb-14">
                    {type !== 'survey' && <CreateOwnContent type={type} content={content} editMode={!!editMode || !!singleProgram} key={refreshContent} />}
                    {type === 'survey' && <ContentSurvey content={content} editMode={surveyId} />}
                </div>
            </div>
        </div>
    )
}

export default Content
